/**
 * The structure dialog's level rows (platform design §5.2.1; Figma `325:11801`, handles
 * deleted -- a level's position is what its parent chain means, so nothing here reorders).
 * Purely presentational: `StructureDialog` owns the staged draft state, the batch write and
 * the consequence preview; this file only renders one row per staged level and reports what
 * the reader did on it.
 */
import { Plus, X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';

import { IconHint } from '../../../chrome/hint';
import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { cn } from '../../../ui/utils';
import { Input } from '../../../ui/input';
import { plural, t } from '../i18n';

/** One staged row: an `existing` level (from the last-read structure, kept, renamed or
 *  toggled for removal) or a `new` one (appended here, never yet saved -- discarding it just
 *  drops it from the array, there is nothing on the server to undo). */
export type DraftLevel =
  | { kind: 'existing'; id: string; name: string; teams: number; removed: boolean }
  | { kind: 'new'; key: string; name: string };

export function Structure(props: {
  rows: DraftLevel[];
  disabled: boolean;
  /** The `key` of the row `onAddLevel` most recently appended -- focused once, not carried
   *  past the row a reader is actually typing into. */
  lastAddedKey: string | null;
  onRename: (index: number, name: string) => void;
  onToggleRemove: (index: number) => void;
  onDiscardNew: (index: number) => void;
  onAddLevel: () => void;
}): ReactNode {
  const { rows, disabled, lastAddedKey, onRename, onToggleRemove, onDiscardNew, onAddLevel } =
    props;
  const locale = getLocale();

  // Imperative, not the `autoFocus` attribute (`jsx-a11y/no-autofocus`): the row a reader
  // just appended by clicking a button, never the page's own initial focus.
  const newRowInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (lastAddedKey !== null) newRowInputRef.current?.focus();
  }, [lastAddedKey]);

  return (
    <ol className="flex flex-col gap-2">
      {rows.map((row, index) => {
        const removed = row.kind === 'existing' && row.removed;
        const position = index + 1;
        return (
          <li
            key={row.kind === 'existing' ? row.id : row.key}
            className={cn('flex flex-wrap items-center gap-3', removed && 'opacity-50')}
          >
            <Badge variant="secondary" className="shrink-0" aria-hidden>
              {position}
            </Badge>
            <Input
              ref={row.kind === 'new' && row.key === lastAddedKey ? newRowInputRef : undefined}
              value={row.name}
              onChange={(event) => onRename(index, event.target.value)}
              aria-label={`${t('teams.editor.level.name.label')} ${position}`}
              disabled={disabled || removed}
              className={cn('max-w-xs', removed && 'line-through')}
            />
            {row.kind === 'existing' && (
              <span className="text-caption text-muted-foreground">
                {plural('teams.editor.level.teamCount.other', row.teams, {
                  count: integer(row.teams, locale),
                })}
              </span>
            )}
            {row.kind === 'existing' && removed ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={() => onToggleRemove(index)}
              >
                {t('teams.editor.undoRemoveLevel')}
              </Button>
            ) : (
              <IconHint label={`${t('teams.editor.removeLevel')} "${row.name}"`}>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="shrink-0"
                  disabled={disabled}
                  onClick={() =>
                    row.kind === 'existing' ? onToggleRemove(index) : onDiscardNew(index)
                  }
                >
                  <X aria-hidden className="size-4" />
                </Button>
              </IconHint>
            )}
          </li>
        );
      })}
      <li>
        <Button
          type="button"
          variant="outline"
          className="w-full justify-start"
          disabled={disabled}
          onClick={onAddLevel}
        >
          <Plus aria-hidden className="size-4" />
          {t('teams.editor.addLevel')}
        </Button>
      </li>
    </ol>
  );
}
